import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '@/lib/api';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

const CartContext = createContext(null);

const API = API_URL;

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], id: null });
  const [cartCount, setCartCount] = useState(0);
  const { token } = useAuth();
  const toast = useToast();

  useEffect(() => {
    const loadCart = async () => {
      try {
        const response = await axios.get(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCart(response.data);
      } catch (error) {
        console.error('Error fetching cart:', error);
      }
    };

    if (token) {
      loadCart();
    }
  }, [token]);

  useEffect(() => {
    const count = cart.items.reduce((sum, item) => sum + item.quantity, 0);
    setCartCount(count);
  }, [cart]);

  const fetchCart = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  };

  const addToCart = async (productId, size, quantity = 1, color = 'Black') => {
    if (!token) {
      toast.info('Login required', 'Sign in to save items to your cart and continue to checkout.');
      return null;
    }
    try {
      const response = await axios.post(
        `${API}/cart`,
        { product_id: productId, size, quantity, color },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Could not add item', error.response?.data?.detail || 'Please try again in a moment.');
      throw error;
    }
  };

  const removeFromCart = async (productId, size, color = 'Black') => {
    if (!token) return;
    try {
      await axios.delete(
        `${API}/remove-cart?product_id=${encodeURIComponent(productId)}&size=${encodeURIComponent(size)}&color=${encodeURIComponent(color)}`,
        {
        headers: { Authorization: `Bearer ${token}` }
        }
      );
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast.error('Could not update cart', error.response?.data?.detail || 'Please try again in a moment.');
    }
  };

  return (
    <CartContext.Provider value={{ cart, cartCount, addToCart, removeFromCart, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
